/**
 * Open by owner — V98-3-3 (lead / admin, org- or program-scoped by the
 * server). One row per holder, plus Unassigned; owners with nothing overdue
 * and nothing projected to miss fold into one "On track" line.
 *
 * Red once (V98-3 review U4): the viewer's own row, and any owner whose
 * items are all already in Owed, are removed (Owed carries them) — so every
 * chip left here keeps its severity tone. A projected hard-date miss counts
 * as attention ("Will miss Oct 11"), even with nothing overdue yet.
 *
 * Every row acts (U1): the item title opens the record; Nudge (shared nudge
 * state, U14); an absent holder → Reassign inline; an unowned item →
 * Assign inline (member picker; reassign previews the receiver first);
 * a projected miss → Resolve on the item.
 *
 * Below 860px the rows stack as cards with the same actions (U2) — the
 * table semantics stay, the text-only summary is gone.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@aigovops/design-system';
import { ASSIGNABLE_KINDS, type BlockerRow } from '../../lib/coverage-api';
import { nudgeMessage, useNudges } from '../../lib/nudge-state';
import { dueChip, firstName, fmtDay, itemHref, ownersTable, type Names, type OwnerRow } from '../../lib/today-model';
import { AssignInline } from './AssignInline';
import { Chip, domId } from './shared';

export function OwnersTable({ rows, viewerId, owedKeys, misses, prefix, slug, todayDay, canAssign, names, focusLater, announce }: {
  rows: readonly BlockerRow[]; viewerId: string; owedKeys: ReadonlySet<string>;
  misses: ReadonlyArray<{ key: string; hard_date: string; user_id?: string | null }>;
  prefix: string; slug: string; todayDay: string;
  /** Owner/admin: inline assign / reassign (the receiver preview is owner/admin-only). */
  canAssign: boolean;
  names: Names;
  focusLater: (id: string) => void; announce: (msg: string) => void;
}) {
  const { attention, onTrack } = ownersTable(rows, viewerId, owedKeys, misses);
  const nudges = useNudges(slug);
  const [open, setOpen] = useState<string | null>(null);

  if (!attention.length && !onTrack.length) {
    return <p className="gp-dim">{rows.length ? 'Everything open is in Owed by you.' : 'Nothing open in your scope.'}</p>;
  }

  const rowId = (o: OwnerRow) => domId('owner', o.user_id ?? 'unassigned');
  const actId = (o: OwnerRow) => domId('nudge', `${o.user_id ?? ''}-${o.top.item_key}`);
  const subjectOf = (o: OwnerRow) => (o.top.ref && ASSIGNABLE_KINDS[o.top.ref.kind] ? { kind: ASSIGNABLE_KINDS[o.top.ref.kind]!, id: o.top.ref.id } : null);
  const toItem = (o: OwnerRow, label: string) => (o.top.ref
    ? <Link className="gp-linkbtn gp-linkbtn--compact" to={itemHref(prefix, o.top.ref)} aria-label={`${label} · ${o.top.title}`}>{label}</Link>
    : null);

  const send = async (o: OwnerRow) => {
    const s = await nudges.send(o.top.item_key, { user_id: o.user_id, name: o.name });
    announce(nudgeMessage(s, o.name));
    focusLater(actId(o));
  };

  const action = (o: OwnerRow) => {
    const id = actId(o);
    const subject = subjectOf(o);
    // R3-S4: the server says per row whether the viewer may assign it (owner/admin or its program lead).
    const assignBtn = (label: string) => ((o.top.can_assign ?? canAssign) && subject
      ? <Button compact id={`${id}-assign`} aria-expanded={open === id} aria-label={`${label} · ${o.top.title}`} onClick={() => setOpen(open === id ? null : id)}>{label}</Button>
      : toItem(o, label === 'Assign' ? 'Open' : label));
    if (!o.user_id) return assignBtn('Assign');
    const s = nudges.state[o.top.item_key];
    if (s?.kind === 'away' || o.willMiss) {
      // The holder is away (or projected to miss): resolve on the item; leads with authority move it here.
      return (
        <span id={id} tabIndex={-1} className="gp-actions">
          {s?.kind === 'away' ? <Chip text={`Away until ${fmtDay(s.until)}`} tone="amber" /> : null}
          {o.willMiss ? toItem(o, 'Resolve') : null}
          {subject ? assignBtn('Reassign')
            // R3-S4: a signer who is away is reassigned from the item's lead actions (signatures aren't assignment subjects).
            : !o.willMiss && o.top.ref?.kind === 'signature_request' && (o.top.can_assign ?? canAssign) ? toItem(o, 'Reassign') : null}
        </span>
      );
    }
    if (s?.kind === 'sent') return <span id={id} tabIndex={-1}><Chip text={`Nudged · ${firstName(s.to)}`} tone="ok" /></span>;
    if (s?.kind === 'already') return <span id={id} tabIndex={-1}><Chip text="Nudged today" tone="lock" /></span>;
    if (s?.kind === 'error') return <span id={id} tabIndex={-1} className="gp-actions"><Chip text={s.text} tone="amber" />{toItem(o, 'Open')}</span>;
    if (o.top.last_nudged_at && o.top.last_nudged_at.slice(0, 10) === todayDay) return <span id={id} tabIndex={-1} className="gp-actions"><Chip text="Nudged today" tone="lock" />{toItem(o, 'Open')}</span>;
    return <Button compact id={id} aria-label={`Nudge ${o.name} · ${o.top.title}`} onClick={() => void send(o)}>Nudge</Button>;
  };

  const due = (o: OwnerRow) => {
    if (o.willMiss) return <Chip text={`Will miss ${fmtDay(o.willMiss.date)}`} tone="red" />;
    if (o.user_id === null) return <Chip text="No owner" tone="amber" />;
    const chip = dueChip(o.top, todayDay);
    return <Chip text={chip.text} tone={chip.tone} />;
  };

  return (
    // R3-U26: explicit ARIA table roles — the stacked cards below 860px set display:block/grid on the table
    // parts, which drops their implicit roles in iOS VoiceOver (and WebKit) unless they are stated.
    <table className="gp-owners" role="table">
      <caption className="gp-sr-only">Open items per owner</caption>
      <thead role="rowgroup"><tr role="row"><th scope="col" role="columnheader">Owner</th><th scope="col" role="columnheader">Open</th><th scope="col" role="columnheader">Most urgent</th><th scope="col" role="columnheader">Due</th><th scope="col" role="columnheader"><span className="gp-sr-only">Action</span></th></tr></thead>
      <tbody role="rowgroup">
        {attention.map((o) => {
          const id = actId(o);
          const subject = subjectOf(o);
          return (
            <tr key={o.user_id ?? 'unassigned'} id={rowId(o)} role="row">
              <th scope="row" role="rowheader">{o.name}</th>
              <td className="gp-num" data-label="Open" role="cell">{o.open}</td>
              <td data-label="Most urgent" role="cell">
                {o.top.ref ? <Link to={itemHref(prefix, o.top.ref)}>{o.top.title}</Link> : o.top.title}
                {o.top.programs.length > 1 ? <span className="gp-dim">{` · ${o.top.programs.length} programs`}</span> : null}
              </td>
              <td data-label="Due" role="cell">{due(o)}</td>
              <td className="gp-owners-act" role="cell">
                {action(o)}
                {open === id && subject ? (
                  <AssignInline slug={slug} subject={subject} title={o.top.title} responsibility={o.top.hat === 'preparer' ? 'owner' : o.top.hat}
                    fromUserId={o.user_id} names={names} openerId={`${id}-assign`}
                    onClose={() => setOpen(null)}
                    onDone={(m) => { setOpen(null); announce(m); focusLater('owners-h'); }} />
                ) : null}
              </td>
            </tr>
          );
        })}
        {onTrack.length ? (
          <tr role="row">
            <th scope="row" role="rowheader">On track</th>
            <td className="gp-num" data-label="Open" role="cell">{onTrack.reduce((n, o) => n + o.open, 0)}</td>
            <td colSpan={3} className="gp-dim" role="cell">{onTrack.map((o) => `${o.name} ${o.open}`).join(' · ')}</td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}
